// The Getting-ready illustration, drawn in the landing's isometric style (same palette and line weight as the machine):
// this computer on the left, the four things Magic Setup moves (shell, agents, projects, sign-ins) crossing a dotted path,
// and the cloud machine on the right. Pure SVG; coordinates are world units, projected by P().

import type { ReactNode } from 'react';

const K = 17; // px per world unit
const C = 0.8660254; // cos 30°
const LINE = '#8A8A8A';
const SOFT = '#BDBDBD';
const BLUE = '#1570EF';
const TINT = '#EFF6FF';
const TINT_LINE = '#84CAFF';

type V3 = [number, number, number];
const P = (x: number, y: number, z: number): [number, number] => [(x - y) * C * K, (x + y) * 0.5 * K - z * K];
const pts = (vs: V3[]) => vs.map((v) => P(...v).map((n) => n.toFixed(1)).join(',')).join(' ');

type BoxSpec = { x: number; y: number; z?: number; w: number; d: number; h: number; top?: string; left?: string; right?: string };

/** Corner points of a box, for fitting the viewBox. */
function corners({ x, y, z = 0, w, d, h }: BoxSpec): V3[] {
  const out: V3[] = [];
  for (const dx of [0, w]) for (const dy of [0, d]) for (const dz of [0, h]) out.push([x + dx, y + dy, z + dz]);
  return out;
}

function Box({ x, y, z = 0, w, d, h, top = '#fff', left = '#F7F7F7', right = '#EDEDED' }: BoxSpec) {
  const z1 = z + h;
  return (
    <g strokeLinejoin="round" stroke={LINE} strokeWidth="1">
      <polygon points={pts([[x, y + d, z], [x + w, y + d, z], [x + w, y + d, z1], [x, y + d, z1]])} fill={left} />
      <polygon points={pts([[x + w, y, z], [x + w, y + d, z], [x + w, y + d, z1], [x + w, y, z1]])} fill={right} />
      <polygon points={pts([[x, y, z1], [x + w, y, z1], [x + w, y + d, z1], [x, y + d, z1]])} fill={top} />
    </g>
  );
}

/** Content drawn on a face. local x runs along the face, local y runs up (or along +Y on a top face). */
const faceLeft = (x: number, y: number, y1: number) => `matrix(${C * K} ${0.5 * K} 0 ${-K} ${P(x, y1, 0)[0]} ${P(x, y1, 0)[1]})`.replace(/\s+/g, ' ') + (y ? '' : '');
const faceRight = (x1: number, y1: number) => `matrix(${C * K} ${-0.5 * K} 0 ${-K} ${P(x1, y1, 0)[0]} ${P(x1, y1, 0)[1]})`;
const faceTop = (x: number, y: number, z: number) => `matrix(${C * K} ${0.5 * K} ${-C * K} ${0.5 * K} ${P(x, y, z)[0]} ${P(x, y, z)[1]})`;

const stroke = (c: string, w = 0.12) => ({ stroke: c, strokeWidth: w, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' });

// ---- the scene (world units; x runs down-right, y down-left, z up) ----
const LAPTOP_BASE: BoxSpec = { x: -9.7, y: 7.2, w: 5, d: 7, h: 0.35 };
const LAPTOP_SCREEN: BoxSpec = { x: -9.7, y: 7.2, z: 0.35, w: 0.25, d: 7, h: 4.4 }; // the lid, tilted back by LID_TILT
const LID_TILT = 1.3;
const CHASSIS: BoxSpec = { x: 3.5, y: -7, w: 8, d: 7, h: 1.6 };
const MODULES: BoxSpec[] = [
  { x: 3.7, y: -6.7, z: 1.6, w: 3.6, d: 3.1, h: 0.55 },
  { x: 7.7, y: -6.7, z: 1.6, w: 3.6, d: 3.1, h: 0.55 },
  { x: 3.7, y: -3.4, z: 1.6, w: 3.6, d: 3.1, h: 0.55 },
  { x: 7.7, y: -3.4, z: 1.6, w: 3.6, d: 3.1, h: 0.55 },
];
// the four things on the move: centred on the line s = x + y = 4
const TILE = 1.5;
const TILE_H = 0.3;
const TILES = [-8.1, -5.0, -1.9, 1.2].map((d, i) => ({ i, x: (4 + d) / 2 - TILE / 2, y: (4 - d) / 2 - TILE / 2, z: 1.0 }));

function Glyph({ i }: { i: number }) {
  if (i === 0)
    return (
      <>
        <path d="M0.3 0.35 0.55 0.6 0.3 0.85" {...stroke(BLUE, 0.13)} />
        <path d="M0.68 0.88h0.25" {...stroke(LINE, 0.13)} />
      </>
    );
  if (i === 1)
    return (
      <>
        <circle cx="0.6" cy="0.6" r="0.3" fill={TINT} stroke={TINT_LINE} strokeWidth="0.1" />
        <circle cx="0.6" cy="0.6" r="0.1" fill={BLUE} />
      </>
    );
  if (i === 2)
    return (
      <>
        <path d="M0.25 0.42h0.28l0.1 0.12h0.38v0.5h-0.76z" {...stroke(LINE, 0.1)} />
        <path d="M0.25 0.7h0.76" {...stroke(TINT_LINE, 0.1)} />
      </>
    );
  return (
    <>
      <circle cx="0.4" cy="0.6" r="0.15" {...stroke(BLUE, 0.11)} />
      <path d="M0.55 0.6h0.4M0.8 0.6v0.2" {...stroke(LINE, 0.11)} />
    </>
  );
}


/** The laptop lid: a thin slab hinged on the back edge of the base and tilted back; the prompt is drawn on its face. */
function Lid() {
  const { x, y, z = 0, w, d, h } = LAPTOP_SCREEN;
  const T = LID_TILT;
  const xf = x + w; // face plane at the hinge
  const A: V3 = [xf, y, z];
  const B: V3 = [xf, y + d, z];
  const Cc: V3 = [xf - T, y + d, z + h];
  const D: V3 = [xf - T, y, z + h];
  const Bb: V3 = [x, y + d, z];
  const Cb: V3 = [x - T, y + d, z + h];
  const Db: V3 = [x - T, y, z + h];
  const tau = T / h;
  const o = P(...B);
  const m = `matrix(${C * K} ${-0.5 * K} ${-tau * C * K} ${-(tau * 0.5 + 1) * K} ${o[0].toFixed(1)} ${o[1].toFixed(1)})`;
  return (
    <g strokeLinejoin="round" stroke={LINE} strokeWidth="1">
      <polygon points={pts([B, Bb, Cb, Cc])} fill="#EDEDED" />
      <polygon points={pts([A, B, Cc, D])} fill="#F4F4F4" />
      <polygon points={pts([D, Cc, Cb, Db])} fill="#fff" />
      <g transform={m} stroke="none">
        <rect x="0.35" y="0.35" width={d - 0.7} height={h - 0.7} rx="0.15" fill="#fff" stroke={SOFT} strokeWidth="0.08" />
        <g {...stroke(BLUE, 0.17)}>
          <path d="M1.2 3.3 2 2.7 1.2 2.1" />
        </g>
        <g {...stroke(LINE, 0.17)}>
          <path d="M2.5 2.7h1.5" />
        </g>
        <g {...stroke(SOFT, 0.17)}>
          <path d="M1.2 1.7h3.6M1.2 1.1h2.4" />
        </g>
      </g>
    </g>
  );
}

export function SetupArt({ className = 'setup-art' }: { className?: string }) {
  const all: V3[] = [
    ...[LAPTOP_BASE, CHASSIS, ...MODULES].flatMap(corners),
    ...corners({ ...LAPTOP_SCREEN, x: LAPTOP_SCREEN.x - LID_TILT, w: LAPTOP_SCREEN.w + LID_TILT }),
    ...TILES.flatMap((t) => corners({ x: t.x, y: t.y, z: t.z, w: TILE, d: TILE, h: TILE_H })),
  ];
  const xs = all.map((v) => P(...v)[0]);
  const ys = all.map((v) => P(...v)[1]);
  const pad = 14;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  const W = Math.max(...xs) + pad - minX;
  const H = Math.max(...ys) + pad - minY;

  // the dotted ground path between the two machines
  const a = P(-4.4, 8.4, 0);
  const b = P(3.5, 0.5, 0);
  const path: ReactNode = <path d={`M${a[0].toFixed(1)} ${a[1].toFixed(1)}L${b[0].toFixed(1)} ${b[1].toFixed(1)}`} stroke={SOFT} strokeWidth="1.4" strokeDasharray="1 5" strokeLinecap="round" fill="none" className="sa-path" />;

  return (
    <svg className={className} viewBox={`${minX.toFixed(1)} ${minY.toFixed(1)} ${W.toFixed(1)} ${H.toFixed(1)}`} width={Math.round(W)} height={Math.round(H)} role="img" aria-label="Your computer's setup moving to your cloud machine" fill="none">
      {/* this computer: the lid first (it is behind the base), tilted back */}
      <Lid />
      <Box {...LAPTOP_BASE} />
      <g transform={faceTop(LAPTOP_BASE.x, LAPTOP_BASE.y, LAPTOP_BASE.h)} {...stroke(SOFT, 0.1)}>
        <path d="M0.8 0.8v5.4M1.5 0.8v5.4M2.2 0.8v5.4M2.9 0.8v5.4" />
        <rect x="3.6" y="2.4" width="1" height="2.2" rx="0.15" />
      </g>

      {path}

      {/* the four things moving over */}
      {TILES.map((t) => (
        <g key={t.i} className="sa-tile" style={{ ['--i' as string]: t.i, animationDelay: `${-t.i * 1.8}s` }}>
         <g className="sa-bob" style={{ animationDelay: `${-t.i * 0.6}s` }}>
          <Box x={t.x} y={t.y} z={t.z} w={TILE} d={TILE} h={TILE_H} />
          <g transform={`${faceTop(t.x, t.y, t.z + TILE_H)} scale(1.25)`}>
            <Glyph i={t.i} />
          </g>
         </g>
        </g>
      ))}

      {/* the cloud machine: chassis, then its four modules, back to front */}
      <Box {...CHASSIS} />
      <g transform={faceLeft(CHASSIS.x, 0, CHASSIS.y + CHASSIS.d)}>
        <rect x="0.5" y="0.45" width="1.7" height="0.7" rx="0.2" fill={TINT} stroke={TINT_LINE} strokeWidth="0.09" />
        <rect x="2.5" y="0.45" width="1.7" height="0.7" rx="0.2" fill={TINT} stroke={TINT_LINE} strokeWidth="0.09" />
        <circle cx="7.4" cy="0.8" r="0.12" fill={BLUE} className="sa-led" />
      </g>
      <g transform={faceRight(CHASSIS.x + CHASSIS.w, CHASSIS.y + CHASSIS.d)} {...stroke(SOFT, 0.09)}>
        <path d="M0.8 0.4h5.4M0.8 0.7h5.4M0.8 1.0h5.4" />
      </g>
      {MODULES.map((m, i) => (
        <g key={i}>
          <Box {...m} />
          <g transform={faceTop(m.x, m.y, m.z! + m.h)} {...stroke(SOFT, 0.1)}>
            <path d="M0.5 0.7h1.4M0.5 1.3h2.4" />
            <circle cx="3.0" cy="0.5" r="0.07" fill={LINE} stroke="none" />
          </g>
        </g>
      ))}
    </svg>
  );
}
